import type { CityConfig } from "../city-template/types";

export const midlothianVaConfig: CityConfig = {
  city: "Midlothian",
  state: "Virginia",
  stateAbbr: "VA",
  slug: "midlothian-va",
  route: "/cities/midlothian-va",
  title: "Emergency Passport Midlothian VA | Expedited Passport Services | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Midlothian, VA — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Midlothian, Chesterfield County, and the Richmond South Corridor. Get your passport fast!",
  h1: "Emergency & Expedited Passport Services in Midlothian, VA",
  heroSubtitle: "Passport expired before your trip? Traveling from Chesterfield County, Brandermill, or the Richmond South Corridor? We help Midlothian travelers get expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Midlothian%20Virginia%20suburban%20landscape%20with%20the%20historic%20Mid-Lothian%20Mines%20Park%2C%20beautiful%20suburban%20homes%20along%20tree-lined%20streets%20and%20rolling%20hills%2C%20warm%20afternoon%20golden%20light%20with%20the%20Swift%20Creek%20Reservoir%20visible%20in%20the%20background%2C%20professional%20photography%20showcasing%20a%20prosperous%20Richmond%20suburb%20with%20green%20forests%20and%20natural%20parks%2C%20no%20people%20visible%2C%20scenic%20suburban%20landscape%20with%20mature%20oak%20trees%20and%20well-kept%20residential%20neighborhoods&width=1800&height=700&seq=midlothian-va-hero-001&orientation=landscape",
  heroImageAlt: "Midlothian Virginia suburban landscape with Mid-Lothian Mines Park and Swift Creek Reservoir near Richmond",
  geoPosition: "37.5059;-77.6492",
  geoPlacename: "Midlothian, Virginia",
  regionalAgency: {
    name: "Washington Passport Agency",
    address: "600 19th Street NW, Washington, DC 20006",
    distance: "Located in downtown Washington, DC — approximately 115 miles north of Midlothian via I-95",
  },
  nearbyCitiesZips: [
    { name: "Midlothian", zips: "23112, 23113, 23114" },
    { name: "Chesterfield County", zips: "23112, 23113, 23114, 23120, 23139, 23832, 23834, 23836, 23838" },
    { name: "Brandermill", zips: "23112" },
    { name: "Woodlake", zips: "23112" },
    { name: "Richmond", zips: "23218, 23219, 23220, 23221, 23222, 23223, 23224, 23225, 23226, 23227, 23228, 23229, 23230, 23231, 23233, 23234, 23235, 23236, 23237, 23238, 23242" },
    { name: "Chester", zips: "23831, 23832, 23834, 23836" },
    { name: "Colonial Heights", zips: "23834" },
    { name: "Petersburg", zips: "23803, 23804, 23805, 23806" },
    { name: "Hopewell", zips: "23860" },
    { name: "Glen Allen", zips: "23059, 23060" },
    { name: "Short Pump", zips: "23233" },
    { name: "Mechanicsville", zips: "23111, 23116" },
    { name: "Ashland", zips: "23005" },
    { name: "Chesterfield", zips: "23832, 23834, 23836, 23838" },
    { name: "Bon Air", zips: "23235" },
    { name: "Moseley", zips: "23120" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent travel departures from the Richmond South Corridor" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-map-pin-2-line", title: "Richmond South Corridor Hub", desc: "Serving Midlothian, Chesterfield County, Brandermill, and the Richmond southern suburbs" },
  ],
  internalLinks: [
    { name: "Virginia state passport services", route: "/states/virginia" },
    { name: "Richmond VA passport services", route: "/cities/richmond-va" },
    { name: "Chester VA passport services", route: "/cities/chester-va" },
    { name: "Glen Allen VA passport services", route: "/cities/glen-allen-va" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Midlothian remotely via secure courier. We do not have a walk-in office in Midlothian — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Midlothian and Richmond South Corridor travelers.",
  fpcOfficeDistance: "Serving all of Midlothian and the Richmond South Corridor including Chesterfield County, Brandermill, Woodlake, Chester, Colonial Heights, and the greater Richmond southern suburbs",
  serviceAreaIntro: "From the historic Mid-Lothian Mines Park to the upscale Brandermill neighborhood, from the Swift Creek Reservoir to the bustling Hull Street Road corridor, from the family-friendly Woodlake community to the growing Chesterfield suburbs — Fast Passport Center serves every corner of Midlothian and the Richmond South Corridor. We regularly help travelers from Chesterfield County, Chester, and Colonial Heights get their passports fast.",
  nearbyCitiesIntro: "We serve all Midlothian neighborhoods and the Richmond South Corridor including Chesterfield County, Brandermill, Woodlake, Chester, Colonial Heights, Richmond, Glen Allen, and Hopewell.",
  howItWorksIntro: "Most Midlothian travelers complete everything online and ship documents overnight. No need to drive to the Washington regional agency or wait in long government lines.",
  mistakesIntro: "Midlothian travelers often face the temptation to drive to the Washington regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Midlothian and Richmond South Corridor travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Midlothian? Here are the most common ones we hear from Richmond South Corridor travelers.",
  statePageRoute: "/states/virginia",
  statePageName: "Virginia",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Midlothian international flights and last-minute travel.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Lisa P.",
      location: "Midlothian, VA",
      rating: 5,
      text: "I live in the Brandermill area and needed an emergency passport for a family cruise to the Bahamas with 6 days notice. Fast Passport Center had my renewal in 4 days. I shipped from the UPS on Midlothian Turnpike and it was completely seamless. No need to drive to DC.",
      service: "Emergency Passport",
    },
    {
      name: "Mark T.",
      location: "Chesterfield, VA",
      rating: 5,
      text: "As a Chesterfield County resident, I was worried about finding expedited passport services close to home. Fast Passport Center made it incredibly easy. Everything was done online, and my renewal was back in 5 days. The convenience for Richmond area families is unbeatable.",
      service: "Passport Renewal",
    },
    {
      name: "Jennifer B.",
      location: "Midlothian, VA",
      rating: 5,
      text: "First passports for my two teenagers before a school trip to Europe. Fast Passport Center handled all the parental consent requirements perfectly and had both passports in 5 days. For busy Midlothian families, this is the best expedited passport service available.",
      service: "Child Passport",
    },
    {
      name: "Robert H.",
      location: "Midlothian, VA",
      rating: 5,
      text: "Lost my passport 3 days before a business trip to Toronto. I was in absolute panic. Fast Passport Center had a replacement in my hands in 48 hours. They saved my trip. Midlothian professionals need this service.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "midlothian-va-1",
      name: "Midlothian Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "Street address pending USPS listing",
      city: "Midlothian",
      state: "VA",
      zip: "23113",
      phone: "(804) 794-2214",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Central Midlothian",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Midlothian facility. Full passport services with appointment. Call ahead to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "midlothian-va-2",
      name: "Chesterfield Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "Street address pending USPS listing",
      city: "Chesterfield",
      state: "VA",
      zip: "23832",
      phone: "(804) 748-1621",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Chesterfield, near Midlothian",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Chesterfield County. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "midlothian-va-3",
      name: "Chester Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "Street address pending USPS listing",
      city: "Chester",
      state: "VA",
      zip: "23831",
      phone: "(804) 748-4321",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Chester, south of Midlothian",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Chester and southern Chesterfield. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "midlothian-va-4",
      name: "Chesterfield County Clerk",
      type: "County Clerk",
      address: "P.O. Box 125",
      city: "Chesterfield",
      state: "VA",
      zip: "23832",
      phone: "(804) 748-1241",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "Chesterfield County",
      website: "https://www.chesterfield.gov",
      websiteLabel: "Chesterfield County Clerk",
      notes: "Provides vital records and birth certificates. Not a passport acceptance facility but useful for document collection.",
      icon: "ri-building-2-line",
    },
    {
      id: "midlothian-va-5",
      name: "Colonial Heights Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "Street address pending USPS listing",
      city: "Colonial Heights",
      state: "VA",
      zip: "23834",
      phone: "(804) 526-1600",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Colonial Heights, south of Midlothian",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Colonial Heights. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "midlothian-va-6",
      name: "Richmond Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "Street address pending USPS listing",
      city: "Richmond",
      state: "VA",
      zip: "23219",
      phone: "(804) 343-1126",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Richmond, north of Midlothian",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves downtown Richmond. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 2+ hour drive to DC", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Midlothian photo shops often use generic sizes. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Richmond area acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Midlothian applicants show up with incorrect payment and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Washington DC regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Midlothian families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Midlothian travelers often assume standard processing will be fine. It takes 8-11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Midlothian or nearby." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Midlothian address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Midlothian before my international trip?", a: "Yes. Fast Passport Center provides emergency passport services in Midlothian for travelers with urgent international departures. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24-48 hour turnaround — perfect for last-minute trips from the Richmond South Corridor." },
    { q: "How fast can I renew my passport in Midlothian?", a: "Fast Passport Center offers fast passport renewal in Midlothian in as little as 24-48 hours through our emergency courier service. For less urgent travel, our 3-5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What is the closest passport agency to Midlothian?", a: "The nearest regional passport agency to Midlothian is the Washington Passport Agency, located approximately 115 miles north in downtown Washington, DC. For urgent processing without the long drive, Fast Passport Center's registered courier service achieves 24-48 hour turnaround from Midlothian." },
    { q: "Can I get a same-day passport appointment near Midlothian?", a: "Same-day passport appointments are only available at the Washington Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24-48 hour emergency courier processing is the fastest guaranteed option for Midlothian residents." },
    { q: "Can I expedite a child passport in Midlothian?", a: "Yes. Fast Passport Center expedites child passports in Midlothian for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Midlothian and Chesterfield County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Midlothian with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24-48 hours." },
    { q: "How fast can I get a passport in Midlothian?", a: "With Fast Passport Center's emergency courier service, Midlothian travelers can receive their passport in as little as 24-48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to drive to the Washington Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities near Midlothian?", a: "Popular options include the Midlothian Post Office (23113), Chesterfield Post Office (23832), Chester Post Office (23831), Colonial Heights Post Office (23834), and Richmond Main Post Office (23219). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "What if I live in Brandermill, Woodlake, or Chesterfield County?", a: "We serve the entire Richmond South Corridor including Brandermill, Woodlake, Chesterfield County, Chester, Colonial Heights, and Hopewell. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Midlothian?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24-48 hour processing, 3-5 day expedited, or standard options. Contact us for a personalized quote." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Midlothian", address: "13901 Midlothian Tpke, Midlothian, VA 23113", hours: "Mon-Sun: 8AM-10PM" },
    { name: "Walgreens — Midlothian", address: "14001 Midlothian Tpke, Midlothian, VA 23113", hours: "Mon-Sun: 7AM-11PM" },
    { name: "CVS Pharmacy — Chesterfield", address: "12501 Jefferson Davis Hwy, Chester, VA 23831", hours: "Mon-Sun: 8AM-10PM" },
    { name: "Walgreens — Chester", address: "12001 Iron Bridge Rd, Chester, VA 23831", hours: "Mon-Sun: 7AM-11PM" },
    { name: "CVS Pharmacy — Colonial Heights", address: "350 Eros Rd, Colonial Heights, VA 23834", hours: "Mon-Sun: 8AM-10PM" },
    { name: "FedEx Office — Richmond", address: "1800 W Broad St, Richmond, VA 23220", hours: "Mon-Fri: 7AM-9PM, Sat: 8AM-6PM" },
  ],
  dmvLocations: [
    {
      name: "Virginia DMV — Midlothian",
      address: "7500 Midlothian Tpke, Richmond, VA 23225",
      hours: "Mon-Fri: 8AM-5PM",
      note: "Driver's license and REAL ID services. Appointment recommended.",
      featured: true,
    },
    {
      name: "Virginia DMV — Chesterfield",
      address: "6701 Iron Bridge Rd, Chesterfield, VA 23832",
      hours: "Mon-Fri: 8AM-5PM",
      note: "REAL ID and passport-related ID services. Appointment strongly recommended.",
      featured: false,
    },
    {
      name: "Virginia DMV — Colonial Heights",
      address: "500 E Roslyn Rd, Colonial Heights, VA 23834",
      hours: "Mon-Fri: 8AM-5PM",
      note: "Serves Colonial Heights and southern Chesterfield. REAL ID available.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "RIC",
      name: "Richmond International Airport",
      distance: "Approximately 18 miles north of Midlothian",
      address: "1 Richard E. Byrd Terminal Dr, Richmond, VA 23250",
      note: "The closest major airport to Midlothian. International and domestic flights. Accessible via I-95 and I-64.",
      flightUrl: "https://www.flyrichmond.com/",
      flightLabel: "RIC Airport",
      featured: true,
    },
    {
      code: "DCA",
      name: "Ronald Reagan Washington National Airport",
      distance: "Approximately 115 miles north of Midlothian",
      address: "2401 Smith Blvd, Arlington, VA 22202",
      note: "Major international hub for Richmond South Corridor travelers. Accessible via I-95.",
      flightUrl: "https://www.flyreagan.com/",
      flightLabel: "DCA Airport",
      featured: false,
    },
    {
      code: "IAD",
      name: "Washington Dulles International Airport",
      distance: "Approximately 135 miles north of Midlothian",
      address: "1 Saarinen Cir, Sterling, VA 20166",
      note: "Major international hub for long-haul flights. Accessible via I-95 and I-495.",
      flightUrl: "https://www.flydulles.com/",
      flightLabel: "IAD Airport",
      featured: false,
    },
    {
      code: "ORF",
      name: "Norfolk International Airport",
      distance: "Approximately 90 miles southeast of Midlothian",
      address: "2200 Norview Ave, Norfolk, VA 23518",
      note: "Regional alternative for southeastern Virginia. Accessible via I-64.",
      flightUrl: "https://www.norfolkairport.com/",
      flightLabel: "ORF Airport",
      featured: false,
    },
  ],
};